import {
  Directive,
  Input,
  ComponentRef,
  ElementRef,
  ViewContainerRef,
  Renderer2,
  ComponentFactoryResolver,
  NgZone,
  TemplateRef,
  forwardRef,
  EventEmitter,
  Output,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from '@angular/core';
import {AbstractControl, ControlValueAccessor, Validator, NG_VALUE_ACCESSOR, NG_VALIDATORS} from '@angular/forms';

import {FishDate} from './fish-date';
import {FishDatepicker, FishDatepickerNavigateEvent} from './datepicker';
import {DayTemplateContext} from './datepicker-day-template-context';
import {FishDateParserFormatter} from './fish-date-parser-formatter';

import {positionElements} from '../util/positioning';
import {FishDateStruct} from './fish-date-struct';
import {FishCalendar} from './fish-calendar';
import {FishDatepickerService} from './datepicker-service';

const FISH_DATEPICKER_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => FishInputDatepicker),
  multi: true
};

const FISH_DATEPICKER_VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => FishInputDatepicker),
  multi: true
};

/**
 * A directive that makes it possible to have datepickers on input fields.
 * Manages integration with the input field itself (data entry) and ngModel (validation etc.).
 */

export class FishInputDatepicker implements OnChanges,
    OnDestroy, ControlValueAccessor, Validator {
  private _cRef: ComponentRef<FishDatepicker> = null;
  private _model: FishDate;
  private _zoneSubscription: any;

  /**
   * Reference for the custom template for the day display
   */
   dayTemplate: TemplateRef<DayTemplateContext>;

  /**
   * Number of months to display
   */
   displayMonths: number;

  /**
  * First day of the week. With default calendar we use ISO 8601: 1=Mon ... 7=Sun
   */
   firstDayOfWeek: number;

  /**
   * Callback to mark a given date as disabled.
   * 'Current' contains the month that will be displayed in the view
   */
   markDisabled: (date: FishDateStruct, current: {year: number, month: number}) => boolean;

  /**
   * Min date for the navigation. If not provided will be 10 years before today or `startDate`
   */
   minDate: FishDateStruct;

  /**
   * Max date for the navigation. If not provided will be 10 years from today or `startDate`
   */
   maxDate: FishDateStruct;

  /**
   * Navigation type: `select` (default with select boxes for month and year), `arrows`
   * (without select boxes, only navigation arrows) or `none` (no navigation at all)
   */
   navigation: 'select' | 'arrows' | 'none';

  /**
   * The way to display days that don't belong to current month: `visible` (default),
   * `hidden` (not displayed) or `collapsed` (not displayed with empty space collapsed)
   */
   outsideDays: 'visible' | 'collapsed' | 'hidden';


  /**
   * Placement of a datepicker popup. Accepts: "top", "bottom", "left", "right", "bottom-left",
   * "bottom-right" etc.
   */
   placement = 'bottom-left';

  /**
   * Whether to display days of the week
   */
   showWeekdays: boolean;

  /**
   * Whether to display week numbers
   */
   showWeekNumbers: boolean;

  /**
   * Date to open calendar with.
   * With default calendar we use ISO 8601: 'month' is 1=Jan ... 12=Dec.
   * If nothing or invalid date provided, calendar will open with current month.
   * Use 'navigateTo(date)' as an alternative
   */
   startDate: {year: number, month: number};

  /**
   * An event fired when navigation happens and currently displayed month changes.
   * See FishDatepickerNavigateEvent for the payload info.
   */
   navigate = new EventEmitter<FishDatepickerNavigateEvent>();

  private _onChange = (_: any) => {};
  private _onTouched = () => {};
  private _validatorChange = () => {};


  constructor(
      private _parserFormatter: FishDateParserFormatter, private _elRef: ElementRef, private _vcRef: ViewContainerRef,
      private _renderer: Renderer2, private _cfr: ComponentFactoryResolver, ngZone: NgZone,
      private _service: FishDatepickerService, private _calendar: FishCalendar) {
    this._zoneSubscription = ngZone.onStable.subscribe(() => {
      if (this._cRef) {
        positionElements(this._elRef.nativeElement, this._cRef.location.nativeElement, this.placement);
      }
    });
  }

  registerOnChange(fn: (value: any) => any): void { this._onChange = fn; }

  registerOnTouched(fn: () => any): void { this._onTouched = fn; }

  registerOnValidatorChange(fn: () => void): void { this._validatorChange = fn; };

  setDisabledState(isDisabled: boolean): void {
    this._renderer.setProperty(this._elRef.nativeElement, 'disabled', isDisabled);
    if (this.isOpen()) {
      this._cRef.instance.setDisabledState(isDisabled);
    }
  }

  validate(c: AbstractControl): {[key: string]: any} {
    const value = c.value;

    if (value === null || value === undefined) {
      return null;
    }

    if (!this._calendar.isValid(value)) {
      return {'fishDate': {invalid: c.value}};
    }

    if (this.minDate && FishDate.from(value).before(FishDate.from(this.minDate))) {
      return {'fishDate': {requiredBefore: this.minDate}};
    }

    if (this.maxDate && FishDate.from(value).after(FishDate.from(this.maxDate))) {
      return {'fishDate': {requiredAfter: this.maxDate}};
    }
  }

  writeValue(value) {
    const fishDate = value ? new FishDate(value.year, value.month, value.day) : null;
    this._model = this._calendar.isValid(value) ? fishDate : null;
    this._writeModelValue(this._model);
  }

  manualDateChange(value: string) {
    this._model = this._service.toValidDate(this._parserFormatter.parse(value), null);
    this._onChange(this._model ? {year: this._model.year, month: this._model.month, day: this._model.day} : value);
    this._writeModelValue(this._model);
  }

  isOpen() { return !!this._cRef; }

  /**
   * Opens the datepicker with the selected date indicated by the ngModel value.
   */
  open() {
    if (!this.isOpen()) {
      const cf = this._cfr.resolveComponentFactory(FishDatepicker);
      this._cRef = this._vcRef.createComponent(cf);

      this._applyPopupStyling(this._cRef.location.nativeElement);
      this._cRef.instance.writeValue(this._model);
      this._applyDatepickerInputs(this._cRef.instance);
      this._subscribeForDatepickerOutputs(this._cRef.instance);
      this._cRef.instance.ngOnInit();

      // date selection event handling
      this._cRef.instance.registerOnChange((selectedDate) => {
        this.writeValue(selectedDate);
        this._onChange(selectedDate);
        this.close();
      });
    }
  }

  /**
   * Closes the datepicker popup.
   */
  close() {
    if (this.isOpen()) {
      this._vcRef.remove(this._vcRef.indexOf(this._cRef.hostView));
      this._cRef = null;
    }
  }

  /**
   * Toggles the datepicker popup (opens when closed and closes when opened).
   */
  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Navigates current view to provided date.
   * With default calendar we use ISO 8601: 'month' is 1=Jan ... 12=Dec.
   * If nothing or invalid date provided calendar will open current month.
   * Use 'startDate' input as an alternative
   */
  navigateTo(date?: {year: number, month: number}) {
    if (this.isOpen()) {
      this._cRef.instance.navigateTo(date);
    }
  }

  onBlur() { this._onTouched(); }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['minDate'] || changes['maxDate']) {
      this._validatorChange();
    }
  }

  ngOnDestroy() {
    this.close();
    this._zoneSubscription.unsubscribe();
  }

  private _applyDatepickerInputs(datepickerInstance: FishDatepicker): void {
    ['dayTemplate', 'displayMonths', 'firstDayOfWeek', 'markDisabled', 'minDate', 'maxDate', 'navigation',
     'outsideDays', 'showNavigation', 'showWeekdays', 'showWeekNumbers']
        .forEach((optionName: string) => {
          if (this[optionName] !== undefined) {
            datepickerInstance[optionName] = this[optionName];
          }
        });
    datepickerInstance.startDate = this.startDate || this._model;
  }

  private _applyPopupStyling(nativeElement: any) {
    this._renderer.addClass(nativeElement, 'dropdown-menu');
    this._renderer.setStyle(nativeElement, 'padding', '0');
  }

  private _subscribeForDatepickerOutputs(datepickerInstance: FishDatepicker) {
    datepickerInstance.navigate.subscribe(date => this.navigate.emit(date));
  }

  private _writeModelValue(model: FishDate) {
    this._renderer.setProperty(this._elRef.nativeElement, 'value', this._parserFormatter.format(model));
    if (this.isOpen()) {
      this._cRef.instance.writeValue(model);
      this._onTouched();
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'input[fishDatepicker]',
  exportAs: 'fishDatepicker',
  host: {'(change)': 'manualDateChange($event.target.value)', '(keyup.esc)': 'close()', '(blur)': 'onBlur()'},
  providers: [FISH_DATEPICKER_VALUE_ACCESSOR, FISH_DATEPICKER_VALIDATOR, FishDatepickerService]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: FishDateParserFormatter, },
{type: ElementRef, },
{type: ViewContainerRef, },
{type: Renderer2, },
{type: ComponentFactoryResolver, },
{type: NgZone, },
{type: FishDatepickerService, },
{type: FishCalendar, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'dayTemplate': [{ type: Input },],
'displayMonths': [{ type: Input },],
'firstDayOfWeek': [{ type: Input },],
'markDisabled': [{ type: Input },],
'minDate': [{ type: Input },],
'maxDate': [{ type: Input },],
'navigation': [{ type: Input },],
'outsideDays': [{ type: Input },],
'placement': [{ type: Input },],
'showWeekdays': [{ type: Input },],
'showWeekNumbers': [{ type: Input },],
'startDate': [{ type: Input },],
'navigate': [{ type: Output },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
